// components/Columns.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CardView from './CardView';
import { isProductRecentlyAdded } from '../utils/productDateUtils';

const HOVER_CLOSE_DELAY_MS = 120;

const getNutrientSource = (row, field) => row?.nutrientSources?.[field] || null;
const isGuaranteedSource = (row, field) => getNutrientSource(row, field) === 'guaranteed_analysis';
const formatNutrientCell = (value, source) => {
  if (value === null || value === undefined) return 'N/A';
  const suffix = source === 'guaranteed_analysis' ? '*' : '';
  return `${(value * 1).toFixed(2)}%${suffix}`;
};

const ProductNameHoverCardCell = ({ params }) => {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef(null);
  const productName = params.value || 'Unnamed product';
  const isRecentlyAdded = isProductRecentlyAdded(params.row.createDate);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openCard = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
    }, HOVER_CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  const closeCardNow = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    clearCloseTimer();
    setOpen(false);
  }, [clearCloseTimer]);

  const stopGridSelection = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  useEffect(() => {
    return () => clearCloseTimer();
  }, [clearCloseTimer]);

  return (
    <Tooltip
      open={open}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      title={
        <Box
          onMouseEnter={openCard}
          onMouseLeave={scheduleClose}
          sx={{
            width: 420,
            maxWidth: '88vw',
            position: 'relative'
          }}
        >
          <IconButton
            size="small"
            aria-label="Close preview card"
            onMouseDown={stopGridSelection}
            onClick={closeCardNow}
            sx={{
              position: 'absolute',
              top: -10,
              right: -10,
              zIndex: 10,
              width: 26,
              height: 26,
              bgcolor: '#2f3b4a',
              color: '#fff',
              border: '2px solid #fff',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
              '&:hover': {
                bgcolor: '#1f2933'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box
            sx={{
              maxHeight: '72vh',
              overflowY: 'auto',
              p: 0.25
            }}
          >
            <CardView cards={[params.row]} isPreview={true} />
          </Box>
        </Box>
      }
      placement="right"
      enterDelay={120}
      leaveDelay={0}
      componentsProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 16]
              }
            }
          ]
        },
        tooltip: {
          sx: {
            p: 0,
            m: '0 !important',
            bgcolor: 'transparent',
            color: '#111',
            border: 'none',
            boxShadow: 'none',
            maxWidth: 'none'
          }
        }
      }}
    >
      <Box
        component="span"
        onMouseEnter={openCard}
        onMouseLeave={scheduleClose}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          width: 'fit-content',
          maxWidth: '100%'
        }}
      >
        <Link
          to={`/Product/${params.row.id}`}
          className="link-style"
          style={{ display: 'inline-block', width: 'fit-content', maxWidth: '100%' }}
        >
          {productName}
        </Link>
        {isRecentlyAdded && (
          <Chip
            label="Recently added"
            size="small"
            color="info"
            sx={{ ml: 1, height: 22, fontWeight: 600 }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

const getColumns = (enums = {}) => [
  {
    field: "brand",
    headerClassName: 'super-app-theme--header',
    headerName: "Brand",
    flex: 1,
    minWidth: 150,
    type: "singleSelect",
    valueOptions: Object.values(enums.brands || {}),
  },
  {
    field: "line",
    headerClassName: 'super-app-theme--header',
    headerName: "Product Line",
    flex: 1,
    minWidth: 150,
  },
  {
    field: "name",
    headerClassName: 'super-app-theme--header',
    headerName: "Product Name",
    flex: 1,
    minWidth: 250,
    renderCell: (params) => <ProductNameHoverCardCell params={params} />
  },
  {
    field: "category",
    headerClassName: 'super-app-theme--header',
    headerName: "Category",
    flex: 1,
    minWidth: 150,
    sortable: false,
    type: "singleSelect",
    valueOptions: Object.values(enums.categories || {}),
  },
  {
    field: "texture",
    headerClassName: 'super-app-theme--header',
    headerName: "Texture",
    flex: 1,
    minWidth: 150,
    sortable: false,
    type: "singleSelect",
    valueOptions: Object.keys(enums.textures || {}),
  },
  {
    field: "format",
    headerClassName: 'super-app-theme--header',
    headerName: "Format",
    flex: 1,
    minWidth: 150,
    sortable: false,
    valueFormatter: (params) => {
      const value = params;
      if (!value) return '';
      return Array.isArray(value) ? value.join(', ') : String(value);
    }
  },
  {
    field: "proteinsDetailed",
    headerClassName: 'super-app-theme--header',
    headerName: "Proteins",
    flex: 1,
    minWidth: 150,
    sortable: false,
    valueFormatter: (params) => {
      const value = params;
      if (!value) return '';
      return Array.isArray(value) ? value.join(', ') : String(value);
    }
  },
  {
    field: "lifestage",
    headerClassName: 'super-app-theme--header',
    headerName: "Lifestage",
    flex: 1,
    minWidth: 150,
    sortable: false,
    valueFormatter: (params) => {
      const value = params;
      if (!value) return '';
      return Array.isArray(value) ? value.join(', ') : String(value);
    }
  },
  {
    field: "healthBenefits",
    headerClassName: 'super-app-theme--header',
    headerName: "Health Benefits",
    flex: 1,
    minWidth: 150,
    sortable: false,
    valueFormatter: (params) => {
      const value = params;
      if (!value) return '';
      return Array.isArray(value) ? value.join(', ') : String(value);
    }
  },
  {
    field: "isGrainFree",
    headerClassName: 'super-app-theme--header',
    headerName: "Grain free",
    flex: 1,
    minWidth: 150,
    sortable: false,
    type: "boolean"
  },
  {
    field: "isGmoFree",
    headerClassName: 'super-app-theme--header',
    headerName: "GMO free",
    flex: 1,
    minWidth: 150,
    sortable: false,
    type: "boolean",
  },
  {
    field: "isLid",
    headerClassName: 'super-app-theme--header',
    headerName: "Limited Ingredient Diet",
    flex: 1,
    minWidth: 150,
    sortable: false,
    type: "boolean",
  },
  {
    field: "containsRawFood",
    headerClassName: 'super-app-theme--header',
    headerName: "Contains Raw Food",
    flex: 1,
    minWidth: 150,
    sortable: false,
    type: "boolean",
    hide: true
  },
  {
    field: "isPrescription",
    headerClassName: 'super-app-theme--header',
    headerName: "Requires prescription",
    flex: 1,
    minWidth: 150,
    sortable: false,
    type: "boolean"
  },
  {
    field: "caloriesPerKg",
    headerClassName: 'super-app-theme--header',
    headerName: "Calories (kcal/kg)",
    flex: 1,
    minWidth: 150,
    type: "number",
    valueFormatter: (params) => {
      return params ? Math.round(params) : 'N/A';
    }
  },
  {
    field: "proteinDryMatter",
    headerClassName: 'super-app-theme--header',
    headerName: "Protein",
    flex: 1,
    minWidth: 150,
    type: "number",
    renderCell: (params) => {
      const value = params.value;
      const source = getNutrientSource(params.row, 'proteinDryMatter');
      return (
        <span style={{ 
          color: isGuaranteedSource(params.row, 'proteinDryMatter') && value !== null ? 'blue' : 'inherit'
        }}>
          {formatNutrientCell(value, source)}
        </span>
      );
    }
  },
  {
    field: "carbsDryMatter",
    headerClassName: 'super-app-theme--header',
    headerName: "Carbs",
    flex: 1,
    minWidth: 150,
    type: "number",
    renderCell: (params) => {
      const value = params.value;
      const source = getNutrientSource(params.row, 'carbsDryMatter');
      return (
        <span style={{ 
          color: isGuaranteedSource(params.row, 'carbsDryMatter') && value !== null ? 'blue' : 'inherit'
        }}>
          {formatNutrientCell(value, source)}
        </span>
      );
    }
  },
  {
    field: "phosphorusDryMatter",
    headerClassName: 'super-app-theme--header',
    headerName: "Phos",
    flex: 1,
    minWidth: 150,
    type: "number",
    renderCell: (params) => {
      const value = params.value;
      const source = getNutrientSource(params.row, 'phosphorusDryMatter');
      return (
        <span style={{ 
          color: isGuaranteedSource(params.row, 'phosphorusDryMatter') && value !== null ? 'blue' : 'inherit'
        }}>
          {formatNutrientCell(value, source)}
        </span>
      );
    }
  },
  {
    field: "sodiumDryMatter",
    headerClassName: 'super-app-theme--header',
    headerName: "Sodium",
    flex: 1,
    minWidth: 150,
    type: "number",
    renderCell: (params) => {
      const value = params.value;
      const source = getNutrientSource(params.row, 'sodiumDryMatter');
      return (
        <span style={{ 
          color: isGuaranteedSource(params.row, 'sodiumDryMatter') && value !== null ? 'blue' : 'inherit'
        }}>
          {formatNutrientCell(value, source)}
        </span>
      );
    }
  },
  {
    field: "calciumDryMatter",
    headerClassName: 'super-app-theme--header',
    headerName: "Calcium",
    flex: 1,
    minWidth: 150,
    type: "number",
    renderCell: (params) => {
      const value = params.value;
      const source = getNutrientSource(params.row, 'calciumDryMatter');
      return (
        <span style={{ 
          color: isGuaranteedSource(params.row, 'calciumDryMatter') && value !== null ? 'blue' : 'inherit'
        }}>
          {formatNutrientCell(value, source)}
        </span>
      );
    }
  },
  {
    field: "fatDryMatter",
    headerClassName: 'super-app-theme--header',
    headerName: "Fat",
    flex: 1,
    minWidth: 150,
    type: "number",
    renderCell: (params) => {
      const value = params.value;
      const source = getNutrientSource(params.row, 'fatDryMatter');
      return (
        <span style={{ 
          color: isGuaranteedSource(params.row, 'fatDryMatter') && value !== null ? 'blue' : 'inherit'
        }}>
          {formatNutrientCell(value, source)}
        </span>
      );
    }
  },
  
  {
    field: "lastUpdateDate",
    headerClassName: 'super-app-theme--header',
    headerName: "Last Updated",
    flex: 1,
    minWidth: 150,
    type: "date",
    valueFormatter: (params) => {
      const value = params;
      if (!value) return 'N/A';
      return new Date(value).toLocaleDateString();
    }
  }
];

export default getColumns;